/**
 * ================================================================
 * Bundle Builder — assets/bundle-builder.js
 * ================================================================
 * Cart update strategy — mirroring what Prestige does internally:
 *
 * Prestige's add-to-cart (ProductForm custom element) works by:
 *   1. Calling /cart/add.js
 *   2. Fetching /?sections=cart-drawer,cart-icon-bubble
 *   3. Calling a method on the live <cart-drawer> element instance
 *      e.g. cartDrawer.renderContents(parsedState)
 *   4. The drawer re-renders itself from inside — listeners intact
 *
 * We do exactly the same thing. The cart-drawer element is already
 * in the DOM; we just grab it and call its method with our sections.
 * ================================================================
 */

const BUNDLE_CONFIG = {
  discountType: 'percentage',   // null | 'percentage' | 'fixed_amount'
  discountValue: 10,
};


class BundleBuilder {

  constructor(el) {
    this.el = el;
    this.sectionId  = el.dataset.sectionId;
    this.totalSteps = parseInt(el.dataset.totalSteps, 10);
    this.isStacked  = el.dataset.layout === 'stacked';

    this.currentStep  = 0;
    this.selections   = {};
    this.productCache = {};

    this.$steps         = [...el.querySelectorAll('.bundle-builder__step')];
    this.$progressItems = [...el.querySelectorAll('[data-progress-step]')];
    this.$progressLines = [...el.querySelectorAll('[data-progress-line]')];
    this.$addToCartBtn  = el.querySelector(`#bundle-add-to-cart-${this.sectionId}`);
    this.$viewCartLink  = el.querySelector(`#bundle-view-cart-${this.sectionId}`);

    this._bindEvents();
    if (this.isStacked) this.$steps.forEach(s => s.classList.add('is-active'));
    el._bbInstance = this; // store for debugging
  }

  // ─── Events ───────────────────────────────────────────────────────────────────

  _bindEvents() {
    this.el.querySelectorAll('.bundle-builder__product-card').forEach(card => {
      card.addEventListener('click', () => this._onProductCardClick(card));
    });
    this.el.querySelectorAll('[data-back-step]').forEach(btn => {
      btn.addEventListener('click', () => this._hideVariantPanel(parseInt(btn.dataset.backStep, 10)));
    });
    this.el.querySelectorAll('[data-confirm-step]').forEach(btn => {
      btn.addEventListener('click', () => this._confirmStep(parseInt(btn.dataset.confirmStep, 10)));
    });
    this.el.addEventListener('click', e => {
      const btn = e.target.closest('[data-edit-step]');
      if (btn) this._goToStep(parseInt(btn.dataset.editStep, 10));
    });
    this.$addToCartBtn?.addEventListener('click', () => this._addToCart());
  }

  // ─── Product Selection ────────────────────────────────────────────────────────

  async _onProductCardClick(card) {
    const handle    = card.dataset.productHandle;
    const stepIndex = parseInt(card.dataset.cardStep, 10);

    this.el.querySelector(`[data-products-grid="${stepIndex}"]`)
      ?.querySelectorAll('.bundle-builder__product-card')
      .forEach(c => { c.classList.remove('is-selected'); c.setAttribute('aria-pressed', 'false'); });

    card.classList.add('is-selected', 'is-loading');
    card.setAttribute('aria-pressed', 'true');

    const product = await this._fetchProduct(handle);
    card.classList.remove('is-loading');
    if (!product) return;

    this._renderVariantPanel(product, stepIndex);
    this._showVariantPanel(stepIndex);
  }

  async _fetchProduct(handle) {
    if (this.productCache[handle]) return this.productCache[handle];
    try {
      const res = await fetch(`/products/${handle}.js`);
      if (!res.ok) throw new Error(`Product fetch failed: ${handle}`);
      const data = await res.json();
      this.productCache[handle] = data;
      return data;
    } catch (err) {
      console.error('[BundleBuilder]', err);
      return null;
    }
  }

  // ─── Variant Panel ────────────────────────────────────────────────────────────

  _showVariantPanel(stepIndex) {
    const panel = this._getPanel(stepIndex);
    panel.hidden = false;
    panel.setAttribute('aria-hidden', 'false');
    requestAnimationFrame(() => requestAnimationFrame(() => panel.classList.add('is-visible')));
    panel.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  _hideVariantPanel(stepIndex) {
    const panel = this._getPanel(stepIndex);
    panel.classList.remove('is-visible');
    panel.addEventListener('transitionend', () => {
      panel.hidden = true;
      panel.setAttribute('aria-hidden', 'true');
    }, { once: true });

    this.el.querySelector(`[data-products-grid="${stepIndex}"]`)
      ?.querySelectorAll('.bundle-builder__product-card')
      .forEach(c => { c.classList.remove('is-selected'); c.removeAttribute('aria-pressed'); });

    const confirmBtn = this.el.querySelector(`[data-confirm-step="${stepIndex}"]`);
    if (confirmBtn) {
      confirmBtn.disabled = true;
      if (confirmBtn.dataset.originalText) confirmBtn.textContent = confirmBtn.dataset.originalText;
    }
  }

  _renderVariantPanel(product, stepIndex) {
    const panel = this._getPanel(stepIndex);
    panel._product = product;
    panel._selectedVariant = null;

    panel.querySelector('.bundle-builder__variant-img').src = product.featured_image || '';
    panel.querySelector('.bundle-builder__variant-img').alt = product.title;
    panel.querySelector('.bundle-builder__variant-name').textContent = product.title;
    this._setPrice(panel, product.price_min);

    const optionsEl = panel.querySelector('.bundle-builder__options');
    optionsEl.innerHTML = '';

    const isSingleDefault =
      product.variants.length === 1 && product.variants[0].title === 'Default Title';

    if (isSingleDefault) {
      const onlyVariant = product.variants[0];
      panel._selectedVariant = onlyVariant.available ? onlyVariant : null;
      const btn = this.el.querySelector(`[data-confirm-step="${stepIndex}"]`);
      if (btn) {
        btn.disabled = !onlyVariant.available;
        if (!onlyVariant.available) btn.textContent = 'Sold Out';
      }
      return;
    }

    product.options.forEach((optionName, optionIndex) => {
      const values = [...new Set(product.variants.map(v => v[`option${optionIndex + 1}`]))];
      const group  = document.createElement('div');
      group.className = 'bundle-builder__option-group';
      group.dataset.optionIndex = optionIndex;

      const label = document.createElement('p');
      label.className = 'bundle-builder__option-name';
      label.textContent = optionName;

      const valuesWrap = document.createElement('div');
      valuesWrap.className = 'bundle-builder__option-values';

      values.forEach(value => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'bundle-builder__option-btn';
        btn.textContent = value;
        btn.dataset.optionIndex = optionIndex;
        btn.dataset.value = value;
        btn.addEventListener('click', () => {
          valuesWrap.querySelectorAll('.bundle-builder__option-btn').forEach(b => {
            b.classList.remove('is-selected'); b.setAttribute('aria-pressed', 'false');
          });
          btn.classList.add('is-selected');
          btn.setAttribute('aria-pressed', 'true');
          this._onOptionSelect(product, stepIndex);
        });
        valuesWrap.appendChild(btn);
      });

      group.appendChild(label);
      group.appendChild(valuesWrap);
      optionsEl.appendChild(group);
    });

    const confirmBtn = this.el.querySelector(`[data-confirm-step="${stepIndex}"]`);
    if (confirmBtn) {
      confirmBtn.disabled = true;
      if (!confirmBtn.dataset.originalText) {
        confirmBtn.dataset.originalText = confirmBtn.textContent.trim();
      }
    }
  }

  _onOptionSelect(product, stepIndex) {
    const panel      = this._getPanel(stepIndex);
    const groups     = [...panel.querySelectorAll('.bundle-builder__option-group')];
    const chosen     = groups.map(g => g.querySelector('.bundle-builder__option-btn.is-selected')?.dataset.value ?? null);
    const confirmBtn = this.el.querySelector(`[data-confirm-step="${stepIndex}"]`);

    this._updateOptionAvailability(product, panel, chosen);

    if (chosen.includes(null)) { panel._selectedVariant = null; confirmBtn.disabled = true; return; }

    const variant = product.variants.find(v =>
      chosen.every((val, i) => val === null || v[`option${i + 1}`] === val)
    );
    panel._selectedVariant = variant?.available ? variant : null;

    if (variant?.available) {
      confirmBtn.disabled = false;
      if (confirmBtn.dataset.originalText) confirmBtn.textContent = confirmBtn.dataset.originalText;
      this._setPrice(panel, variant.price);
      if (variant.featured_image) {
        panel.querySelector('.bundle-builder__variant-img').src =
          variant.featured_image.src || variant.featured_image;
      }
    } else {
      confirmBtn.disabled = true;
      confirmBtn.textContent = variant ? 'Out of stock' : 'Unavailable';
    }
  }

  _updateOptionAvailability(product, panel, chosen) {
    panel.querySelectorAll('.bundle-builder__option-btn').forEach(btn => {
      const oIdx = parseInt(btn.dataset.optionIndex, 10);
      const test = [...chosen];
      test[oIdx] = btn.dataset.value;
      const ok = product.variants.some(v =>
        test.every((val, i) => val === null || v[`option${i + 1}`] === val) && v.available
      );
      btn.classList.toggle('is-unavailable', !ok);
      btn.setAttribute('aria-disabled', String(!ok));
    });
  }

  _setPrice(panel, priceCents) {
    const el = panel.querySelector('.bundle-builder__variant-price');
    if (!el) return;
    el.textContent = window.Shopify?.formatMoney
      ? Shopify.formatMoney(priceCents, window.theme?.moneyFormat || '${{amount}}')
      : new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(priceCents / 100);
  }

  _getPanel(stepIndex) {
    return this.el.querySelector(`[data-variant-panel="${stepIndex}"]`);
  }

  // ─── Step Navigation ──────────────────────────────────────────────────────────

  _confirmStep(stepIndex) {
    const panel   = this._getPanel(stepIndex);
    const variant = panel._selectedVariant;
    const product = panel._product;
    if (!variant?.available || !product) return;

    this.selections[stepIndex] = {
      productHandle: product.handle,
      productTitle:  product.title,
      variantId:     variant.id,
      variantTitle:  variant.title,
      price:         variant.price,
      image:         variant.featured_image?.src || product.featured_image || '',
    };

    this.$steps[stepIndex]?.classList.add('is-complete');
    this.$progressItems[stepIndex]?.classList.add('is-complete');
    if (stepIndex > 0) this.$progressLines[stepIndex - 1]?.classList.add('is-complete');
    this._updateSummaryItem(stepIndex);

    const next = stepIndex + 1;
    next < this.totalSteps ? this._goToStep(next) : this._onAllComplete();
  }

  _goToStep(index) {
    if (index < 0 || index >= this.totalSteps) return;
    if (this.isStacked) {
      this.$steps[index]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      this.$steps[index]?.classList.add('is-highlighted');
      setTimeout(() => this.$steps[index]?.classList.remove('is-highlighted'), 1200);
    } else {
      this.$steps.forEach((s, i) => s.classList.toggle('is-active', i === index));
      requestAnimationFrame(() => this.el.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    }
    this.$progressItems.forEach((item, i) => item.classList.toggle('is-active', i === index));
    this.currentStep = index;
  }

  _onAllComplete() {
    // Verify every step has a selection before enabling the button
    const completedCount = Object.keys(this.selections).length;
    if (completedCount < this.totalSteps) return;

    this.$addToCartBtn.disabled = false;
    this.$addToCartBtn.setAttribute('aria-disabled', 'false');
    this.$addToCartBtn.classList.add('is-ready');
    requestAnimationFrame(() => {
      this.el.querySelector('.bundle-builder__summary')
        ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  }

  // ─── Summary ──────────────────────────────────────────────────────────────────

  _updateSummaryItem(stepIndex) {
    const sel  = this.selections[stepIndex];
    const item = this.el.querySelector(`[data-summary-item="${stepIndex}"]`);
    if (!sel || !item) return;

    const variantLabel = sel.variantTitle && sel.variantTitle !== 'Default Title'
      ? ` — ${sel.variantTitle}` : '';

    item.querySelector('.bundle-builder__summary-selection').textContent =
      sel.productTitle + variantLabel;
    item.querySelector('.bundle-builder__summary-selection').classList.add('has-selection');
    item.classList.add('is-complete');
    item.querySelector('.bundle-builder__summary-num')?.classList.add('is-complete');
    const editBtn = item.querySelector('[data-edit-step]');
    if (editBtn) editBtn.hidden = false;
  }

  // ─── Add to Cart ──────────────────────────────────────────────────────────────

  async _addToCart() {
    const completedCount = Object.keys(this.selections).length;
    if (completedCount < this.totalSteps) {
      for (let i = 0; i < this.totalSteps; i++) {
        if (!this.selections[i]) {
          const stepEl = this.$steps[i];
          stepEl?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          stepEl?.classList.add('is-highlighted');
          setTimeout(() => stepEl?.classList.remove('is-highlighted'), 1500);
          const label = this.$addToCartBtn.querySelector('.bundle-builder__atc-label');
          if (label) {
            const orig = label.textContent;
            label.textContent = `Please complete Step ${i + 1} first`;
            setTimeout(() => { label.textContent = orig; }, 3000);
          }
          return;
        }
      }
      return;
    }

    const bundleId = this._generateBundleId();

    // ── Step 1: Ask CartDrawer which sections it needs rendered ──────────────
    // Mirrors exactly what ProductForm does before calling /cart/add.js.
    // CartDrawer listens for this event and pushes its section ID into the array.
    const sectionsToBundle = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
      bubbles: true,
      detail: { sections: sectionsToBundle }
    }));

    // ── Step 2: Build line items ──────────────────────────────────────────────
    const items = Object.entries(this.selections).map(([stepIndex, sel]) => {
      const properties = {
        _bundle_id:   bundleId,
        _bundle_step: `Step ${parseInt(stepIndex, 10) + 1}`,
      };
      if (BUNDLE_CONFIG.discountType && BUNDLE_CONFIG.discountValue > 0) {
        properties._bundle_discount = BUNDLE_CONFIG.discountType === 'percentage'
          ? `${BUNDLE_CONFIG.discountValue}%`
          : `${BUNDLE_CONFIG.discountValue / 100} off`;
      }
      return { id: sel.variantId, quantity: 1, properties };
    });

    this._setCartBtnState('loading');

    try {
      // ── Step 3: Add to cart, requesting section HTML in the response ────────
      // Shopify renders the requested sections and returns them in responseJson.sections
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items,
          sections: sectionsToBundle.join(','),
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || err.message || `HTTP ${res.status}`);
      }

      const responseJson = await res.json();

      // ── Step 4: Fetch updated cart state ─────────────────────────────────────
      const cartContent = await fetch('/cart.js').then(r => r.json());

      // Attach the rendered section HTML (same as ProductForm does)
      cartContent['sections'] = responseJson['sections'] || {};

      // ── Step 5: Dispatch cart:change — CartDrawer handles everything else ────
      // CartDrawer listens for this event. When baseEvent === 'variant:add' and
      // cartType === 'drawer', it calls replaceContent(sections[sectionId])
      // to update its own HTML, then calls this.show() to open itself.
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
        bubbles: true,
        detail: {
          baseEvent: 'variant:add',
          cart: cartContent,
        }
      }));

      this._setCartBtnState('success');
      this._showViewCartLink();

    } catch (err) {
      console.error('[BundleBuilder] Add to cart failed:', err);
      this._setCartBtnState('error', err.message);
    }
  }

  // ─── Prestige Cart Update ────────────────────────────────────────────────────


  _injectLineItemsOnly(sectionHtml) {
    if (!sectionHtml) return;
    const parsed = new DOMParser().parseFromString(sectionHtml, 'text/html');

    // Only replace the line items list and cart total — NOT the drawer wrapper.
    // These elements don't have open/close listeners so replacing them is safe.
    const safeSelectors = [
      '#CartDrawerItems',          // Prestige cart items container
      '#CartItems',
      '.cart-drawer__cart-items-wrapper',
      '.js-contents',
      '[id*="cart-items"]',
      '[class*="cart-items"]',
      '.cart__items',
      '#cart-icon-bubble',
    ];

    safeSelectors.forEach(sel => {
      const existing = document.querySelector(sel);
      const fresh    = parsed.querySelector(sel);
      if (existing && fresh) {
        existing.replaceWith(fresh.cloneNode(true));
      }
    });
  }





  // ─── Button State / View Cart ─────────────────────────────────────────────────

  _showViewCartLink() {
    if (!this.$viewCartLink) return;
    this.$viewCartLink.hidden = false;
    requestAnimationFrame(() => this.$viewCartLink.classList.add('is-visible'));
  }

  _setCartBtnState(state, errorMessage = null) {
    const btn     = this.$addToCartBtn;
    const label   = btn.querySelector('.bundle-builder__atc-label');
    const spinner = btn.querySelector('.bundle-builder__atc-spinner');
    btn.classList.remove('is-loading', 'is-success', 'is-error');

    if (state === 'loading') {
      btn.disabled = true; label.hidden = true; spinner.hidden = false;
      btn.classList.add('is-loading');
    } else if (state === 'success') {
      btn.disabled = false; label.hidden = false; spinner.hidden = true;
      label.textContent = 'Added to Cart ✓';
      btn.classList.add('is-success');
      setTimeout(() => { label.textContent = 'Add Bundle to Cart'; btn.classList.remove('is-success'); }, 3500);
    } else if (state === 'error') {
      btn.disabled = false; label.hidden = false; spinner.hidden = true;
      // Show Shopify's actual error if available (e.g. "already sold out"), else generic
      label.textContent = errorMessage || 'Something went wrong — try again';
      btn.classList.add('is-error');
      setTimeout(() => { label.textContent = 'Add Bundle to Cart'; btn.classList.remove('is-error'); }, 5000);
    }
  }

  _generateBundleId() {
    return typeof crypto?.randomUUID === 'function'
      ? crypto.randomUUID().replace(/-/g, '').substring(0, 12)
      : Math.random().toString(36).substring(2, 14).toUpperCase();
  }
}


// ─── Init ─────────────────────────────────────────────────────────────────────

function initBundleBuilders() {
  document.querySelectorAll('.bundle-builder:not([data-bb-init])').forEach(el => {
    el.dataset.bbInit = 'true';
    new BundleBuilder(el);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initBundleBuilders);
} else {
  initBundleBuilders();
}

document.addEventListener('shopify:section:load', e => {
  const el = e.target.querySelector('.bundle-builder');
  if (el) { delete el.dataset.bbInit; initBundleBuilders(); }
});